import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/motion";
import { Reveal, Overline } from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { useSEO } from "@/lib/useSEO";

const PRINCIPLES = [
  { t: "Comprendre avant d'agir", d: "Chaque intervention débute par une lecture approfondie du contexte, des enjeux et des réalités opérationnelles du projet." },
  { t: "Structurer avec méthode", d: "Nous mobilisons une méthodologie propriétaire, éprouvée et confidentielle, qui séquence et sécurise chaque étape du projet." },
  { t: "Gouverner la complexité", d: "Nous installons les cadres de décision et de redevabilité qui garantissent l'alignement durable des parties prenantes." },
  { t: "Mesurer l'impact", d: "Nous inscrivons la performance et la valeur durable au cœur de chaque projet, au-delà de sa seule réalisation." },
];

export default function Approche() {
  useSEO({
    fullTitle: "Méthodologie de gouvernance des projets | HERUS",
    description:
      "Découvrez l'approche HERUS et SIIGMAA+G®, méthodologie de diagnostic, structuration et gouvernance des projets stratégiques.",
    keywords: "méthodologie de gouvernance des projets, approche HERUS, SIIGMAA+G, diagnostic, structuration de projets, gouvernance, conseil stratégique",
    path: "/approche-gouvernance-projets",
  });

  return (
    <div data-testid="approche-page" className="bg-[#0A0A0A]">
      {/* Hero */}
      <section className="pt-40 lg:pt-52 pb-20 mx-auto max-w-[1600px] px-6 lg:px-12">
        <Reveal><Overline>Notre approche</Overline></Reveal>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="mt-8 font-serif-display font-light leading-[0.95] tracking-tight text-[#F9F6F0] text-4xl sm:text-5xl lg:text-7xl max-w-5xl"
        >
          Notre <span className="italic text-[#C5A059]">approche.</span>
        </motion.h1>
        <Reveal i={2}>
          <p className="mt-10 max-w-2xl text-[#F9F6F0]/70 text-base lg:text-lg font-light leading-relaxed">
            Chaque mission présente une combinaison unique d'enjeux institutionnels, techniques,
            économiques et humains. C'est pourquoi HERUS privilégie une approche globale, fondée sur
            l'analyse systémique, la compréhension des interactions entre les parties prenantes et
            l'alignement des décisions avec les objectifs de long terme.
          </p>
        </Reveal>
      </section>

      {/* Network band — interactions & systèmes */}
      <section className="relative h-[38vh] min-h-[280px] overflow-hidden border-y border-white/10 bg-[#0A0A0A]">
        <img src={IMAGES.networkNight} alt="Réseau de transport de nuit — interconnexions, flux et systèmes complexes" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40" />
        <div className="relative z-10 h-full flex items-end mx-auto max-w-[1600px] px-6 lg:px-12 pb-10">
          <p className="font-serif-display text-2xl lg:text-3xl font-light italic text-[#F9F6F0]/90 max-w-2xl">
            Lire les interactions, éclairer les décisions.
          </p>
        </div>
      </section>

      {/* Principles */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-20 pb-8">
        <div className="grid md:grid-cols-2 border-t border-l border-white/10">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={i} i={i} className="border-b border-r border-white/10 p-10 lg:p-14">
              <span className="font-serif-display text-5xl text-[#C5A059] leading-none block">0{i + 1}</span>
              <h3 className="mt-5 font-serif-display text-2xl lg:text-3xl text-[#F9F6F0]">{p.t}</h3>
              <p className="mt-3 text-[#F9F6F0]/60 font-light leading-relaxed">{p.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Proprietary methodology — SIIGMAA+G® */}
      <section className="relative overflow-hidden border-t border-white/10 mt-12 bg-[#0A0A0A]">
        <ParticleField opacity={1.3} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/20 via-transparent to-[#0A0A0A]/20" />
        <div className="relative z-10 mx-auto max-w-3xl px-6 py-28 lg:py-40">
          <Reveal><Overline>Méthodologie propriétaire · SIIGMAA+G®</Overline></Reveal>
          <Reveal i={1}>
            <h2 className="mt-8 font-serif-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] text-[#F9F6F0]">
              SIIGMAA+G® — une méthode pour révéler ce qui conditionne réellement la{" "}
              <span className="italic text-[#C5A059]">réussite d'un projet.</span>
            </h2>
          </Reveal>
          <div className="mt-10 space-y-7">
            <Reveal i={2}>
              <p className="text-[#F9F6F0]/70 text-base lg:text-lg font-light leading-relaxed">
                Développée par HERUS, SIIGMAA+G® est une méthodologie propriétaire conçue pour analyser
                les projets stratégiques dans leur globalité. Elle permet d'identifier les
                vulnérabilités structurelles, de mettre en évidence les facteurs critiques de réussite
                et d'éclairer les décisions avant que les difficultés ne deviennent coûteuses ou
                irréversibles.
              </p>
            </Reveal>
            <Reveal i={3}>
              <p className="text-[#F9F6F0]/70 text-base lg:text-lg font-light leading-relaxed">
                Fondée sur une approche systémique, elle mobilise un référentiel d'analyse structuré
                qui croise les dimensions stratégiques, institutionnelles, organisationnelles, humaines
                et opérationnelles afin de produire un diagnostic objectif et directement exploitable.
              </p>
            </Reveal>
            <Reveal i={4}>
              <p className="text-[#F9F6F0]/55 text-base lg:text-lg font-light leading-relaxed border-l-2 border-[#C5A059]/50 pl-6">
                Les principes, outils d'analyse, matrices d'évaluation et critères d'appréciation qui
                composent SIIGMAA+G® constituent un actif intellectuel propre à HERUS. Ils sont
                exclusivement mobilisés dans le cadre des missions conduites pour nos clients.
              </p>
            </Reveal>
          </div>
          <Reveal i={5}>
            <Link to="/contact" data-testid="approche-cta" className="group inline-flex items-center gap-3 mt-12 text-sm uppercase tracking-[0.2em] text-[#F9F6F0]/80 hover:text-[#C5A059] border-b border-white/20 hover:border-[#C5A059] pb-2 transition-colors duration-500">
              Prendre contact
              <ArrowUpRight size={16} className="text-[#C5A059] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
